import { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize } from '../utils/localization';

import invertedMessages from './messages/activationBanner';

interface ActivationBannerReturn {
  // Success banner props
  successBanner: {
    isVisible: boolean;
    message: string;
    onClose: () => void;
  };
  // Error banner props
  errorBanner: {
    isVisible: boolean;
    message: string;
    onClose: () => void;
  };
}

/**
 * Custom hook for managing rule activation feedback banners
 * Checks sessionStorage for activation success/error data and manages banner state
 */
const useActivationBanner = (): ActivationBannerReturn => {
  const l = useInvertedLocalize(invertedMessages);

  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check for rule activation feedback from sessionStorage
  useEffect(() => {
    try {
      // Check for success data
      const successData = sessionStorage.getItem('ruleActivationSuccess');

      if (successData) {
        const parsed = JSON.parse(successData) as {
          ruleName: string;
          ruleId: string;
          timestamp: number;
        };
        const { ruleName, timestamp } = parsed;

        // Safety check: only show if recent (prevent stale data from crashes)
        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setSuccessMessage(
            l('Your {ruleName} Rule has been activated!', { ruleName })
          );
          setShowSuccessBanner(true);
        }

        sessionStorage.removeItem('ruleActivationSuccess'); // always clean up
      }

      // Check for error data
      const errorData = sessionStorage.getItem('ruleActivationError');

      if (errorData) {
        const parsed = JSON.parse(errorData) as {
          ruleName: string;
          errorMessage: string;
          timestamp: number;
        };
        const { ruleName, timestamp } = parsed;

        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setErrorMessage(
            l("Your rule {ruleName} couldn't be activated.", { ruleName })
          );
          setShowErrorBanner(true);
        }

        sessionStorage.removeItem('ruleActivationError');
      }
    } catch (parseError) {
      Logger.error('Error processing activation feedback:', parseError);
      // Clean up corrupted data
      sessionStorage.removeItem('ruleActivationSuccess');
      sessionStorage.removeItem('ruleActivationError');
    }
  }, [l]);

  return {
    successBanner: {
      isVisible: showSuccessBanner,
      message: successMessage,
      onClose: () => setShowSuccessBanner(false),
    },
    errorBanner: {
      isVisible: showErrorBanner,
      message: errorMessage,
      onClose: () => setShowErrorBanner(false),
    },
  };
};

export default useActivationBanner;
